import { GapsWidget } from '@/features/gaps/GapsWidget';
import { RsiWidget } from '@/features/rsi/RsiWidget';
import { PointsWidget } from '@/features/time-windows/PointsWidget';
import { KnotsWidget } from '@/features/knots/KnotsWidget';
import { FibonacciWidget } from '@/features/fibonacci/FibonacciWidget';
import { VolumeWidget } from '@/features/volume/VolumeWidget';
import { useSymbol } from '@/lib/useSymbol';
import { FeedStatus } from '@/components/FeedStatus';
import { useI18n } from '@/lib/i18n';
import { useRef } from 'react';
import { MODULES_BY_IMPORTANCE, type ModuleId } from '@pipyar/core';
import { useFillHeight } from '@/lib/useFillHeight';

type ColumnModule = Exclude<ModuleId, 'points'>;

/** Modules shown as columns. Time points have their own row above them. */
const WIDGETS: Record<ColumnModule, () => React.JSX.Element> = {
  fibonacci: FibonacciWidget,
  knots: KnotsWidget,
  volume: VolumeWidget,
  gaps: GapsWidget,
  rsi: RsiWidget,
};

/** The time points timeline under the columns, and the gap above it. */
const TIMELINE_HEIGHT_PX = 188;
const TIMELINE_RESERVE_PX = TIMELINE_HEIGHT_PX + 16;

const COLUMN_MODULES = MODULES_BY_IMPORTANCE.filter((id): id is ColumnModule => id !== 'points');

/**
 * The dashboard is a board of widgets, each owned by a feature module.
 *
 * Keeping the widgets in their feature folder rather than here means a module
 * brings its own overview with it, and this page stays a layout rather than a
 * place where every feature's logic accumulates.
 */
export function DashboardPage() {
  const { t } = useI18n();
  const { symbol } = useSymbol();
  const rowRef = useRef<HTMLDivElement | null>(null);
  // Columns fill the window below the header, less room for the timeline.
  const height = useFillHeight(rowRef, true, 320, TIMELINE_RESERVE_PX);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('dashboard.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('dashboard.subtitle', { symbol: symbol ?? '—' })}
          </p>
        </div>
        <div className="ms-auto">
          <FeedStatus />
        </div>
      </div>

      {/*
       * The price modules: one row of full-height columns in MODULE_IMPORTANCE
       * order from the start edge, so the module you trust most is always the
       * first column you read.
       *
       * Equal columns rather than masonry: every module gets the same share of
       * the screen and the same place every time. Each column fills the window
       * down to the dock and scrolls inside itself. Where they do not fit side
       * by side the row scrolls sideways, snapping to a column at a time.
       */}
      <div
        ref={rowRef}
        style={height !== null ? { height } : undefined}
        className="scrollbar-slim -mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-1 sm:-mx-6 sm:px-6 xl:mx-0 xl:px-0"
      >
        {COLUMN_MODULES.map((id) => {
          const Widget = WIDGETS[id];
          return (
            <div
              key={id}
              className="h-full w-[min(18rem,82vw)] shrink-0 snap-start xl:w-auto xl:min-w-[12rem] xl:flex-1"
            >
              <Widget />
            </div>
          );
        })}
      </div>

      {/*
       * Time points as a timeline bar under the columns: it is the one module
       * about WHEN rather than WHERE, so it runs along the bottom as a time
       * axis instead of competing for a column. Fixed height; the columns
       * above leave exactly this much room for it (TIMELINE_RESERVE).
       */}
      <div style={{ height: TIMELINE_HEIGHT_PX }}>
        <PointsWidget />
      </div>
    </div>
  );
}
